import React, { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import { Button } from '../components/ui/button';
import { useAgentsQuery } from '@cotera/client/app/hooks/use-agents-display-names';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { Loading, toast } from '@cotera/client/app/components';
import { useSlackBotSettingsModel } from './slack-bot-settings.model';
import { ChatBotMappingSectionLoading } from './chat-bot-mapping-section-loading';
import { ShareAgentWithOrgDialog } from './share-agent-with-org-dialog';

const DEFAULT_AGENT_VALUE = '__default__';

export const SlackBotChannelAgentsSection: React.FC = () => {
  const model = useSlackBotSettingsModel();
  const status = useWatchableValue(model.status);
  const config = useWatchableValue(model.config);
  const pendingChannelId = useWatchableValue(model.pendingChannelId);
  const { data: agentsData } = useAgentsQuery();
  const agents = useMemo(() => agentsData?.agents ?? [], [agentsData]);
  const [draftChannelId, setDraftChannelId] = useState('');
  const [draftAgentId, setDraftAgentId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [shareConfirm, setShareConfirm] = useState<{
    channelId: string;
    agentId: string;
  } | null>(null);
  const [isSharing, setIsSharing] = useState(false);

  const shareAgentName = useMemo(() => {
    if (shareConfirm === null) {
      return '';
    }
    const agent = agents.find(
      (candidate) => candidate.id === shareConfirm.agentId
    );
    return agent?.title ?? agent?.agentKey ?? 'This agent';
  }, [shareConfirm, agents]);

  // Assigns a channel agent, opening the share-confirmation dialog when the
  // agent isn't shared with the org yet. Returns true only when the mapping was
  // actually saved.
  const assignChannelAgent = async (
    channelId: string,
    agentId: string,
    confirmShare: boolean
  ): Promise<boolean> => {
    try {
      const { needsShare } = await model.setChannelAgent(
        channelId,
        agentId,
        confirmShare
      );
      if (needsShare) {
        setShareConfirm({ channelId, agentId });
        return false;
      }
      return true;
    } catch {
      toast.error('Failed to update Slack channel agent');
      return false;
    }
  };

  const handleConfirmShare = async (): Promise<void> => {
    if (shareConfirm === null) {
      return;
    }
    const { channelId, agentId } = shareConfirm;
    setIsSharing(true);
    try {
      const assigned = await assignChannelAgent(channelId, agentId, true);
      if (assigned) {
        setShareConfirm(null);
        if (channelId === draftChannelId && agentId === draftAgentId) {
          setDraftChannelId('');
          setDraftAgentId('');
        }
        toast.success('Agent shared with your organization and assigned');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const channelItems = useMemo(
    () =>
      config.channels.map((channel) => ({
        value: channel.channelId,
        label:
          channel.channelName === channel.channelId
            ? channel.channelId
            : `#${channel.channelName}`,
      })),
    [config.channels]
  );

  const mappedChannels = config.channels.filter(
    (channel) => channel.agentId !== null
  );

  if (status === 'loading') {
    return (
      <ChatBotMappingSectionLoading
        title="Channel agents"
        description="Assign a Cotera agent per Slack channel. Channels without an assignment use Coco's default Slack behavior."
      />
    );
  }

  if (config.installed !== true) {
    return null;
  }

  const handleChannelAgentChange = async (
    channelId: string,
    nextValue: string
  ): Promise<void> => {
    if (nextValue === DEFAULT_AGENT_VALUE) {
      try {
        await model.clearChannelAgent(channelId);
      } catch {
        toast.error('Failed to update Slack channel agent');
      }
      return;
    }

    const assigned = await assignChannelAgent(channelId, nextValue, false);
    if (assigned) {
      toast.success('Channel agent saved');
    }
  };

  const handleRemoveMapping = async (channelId: string): Promise<void> => {
    try {
      await model.clearChannelAgent(channelId);
      toast.success('Channel mapping removed');
    } catch {
      toast.error('Failed to remove Slack channel mapping');
    }
  };

  const handleAddMapping = async (): Promise<void> => {
    if (draftChannelId.length === 0) {
      toast.error('Select a Slack channel');
      return;
    }
    if (draftAgentId.length === 0) {
      toast.error('Select an agent');
      return;
    }

    setIsAdding(true);
    try {
      const assigned = await assignChannelAgent(
        draftChannelId,
        draftAgentId,
        false
      );
      if (assigned) {
        setDraftChannelId('');
        setDraftAgentId('');
        toast.success('Channel agent saved');
      }
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <ShareAgentWithOrgDialog
        open={shareConfirm !== null}
        agentName={shareAgentName}
        isSharing={isSharing}
        onOpenChange={(open) => {
          if (!open && !isSharing) {
            setShareConfirm(null);
          }
        }}
        onConfirm={() => {
          void handleConfirmShare();
        }}
      />
      <div>
        <p className="text-sm font-medium">Channel agents</p>
        <p className="text-xs text-muted-foreground">
          Assign a Cotera agent per Slack channel. Channels without an
          assignment use Coco&apos;s default Slack behavior.
        </p>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <p className="text-xs font-medium text-muted-foreground">
          Add channel mapping
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Slack channel</p>
            <Combobox
              items={channelItems}
              value={draftChannelId}
              onChange={setDraftChannelId}
              placeholder="Search or enter channel ID..."
              searchPlaceholder="Search channels..."
              emptyMessage="Type a channel ID if none are listed"
              allowCustomValues={true}
              className="w-full"
            />
          </div>
          <div className="flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">Agent</p>
            <Select value={draftAgentId} onValueChange={setDraftAgentId}>
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.title ?? agent.agentKey}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            className="h-9"
            disabled={isAdding}
            onClick={() => {
              void handleAddMapping();
            }}
          >
            {isAdding ? (
              <>
                <Loading.Spinner variant="sm" />
                Saving...
              </>
            ) : (
              'Save mapping'
            )}
          </Button>
        </div>
      </div>

      {config.channelsError !== undefined && (
        <p className="text-xs text-destructive">{config.channelsError}</p>
      )}

      {mappedChannels.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No channel mappings yet. Add one above to route a Slack channel to a
          specific agent.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Configured mappings
          </p>
          {mappedChannels.map((channel) => {
            const selectValue =
              channel.agentId !== null ? channel.agentId : DEFAULT_AGENT_VALUE;
            const channelLabel =
              channel.channelName === channel.channelId
                ? channel.channelId
                : `#${channel.channelName}`;

            return (
              <div
                key={channel.channelId}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <p className="text-sm">{channelLabel}</p>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectValue}
                    disabled={pendingChannelId === channel.channelId}
                    onValueChange={(nextValue) => {
                      void handleChannelAgentChange(
                        channel.channelId,
                        nextValue
                      );
                    }}
                  >
                    <SelectTrigger className="h-8 w-[220px]">
                      <SelectValue placeholder="Default Coco" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DEFAULT_AGENT_VALUE}>
                        Default Coco
                      </SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.title ?? agent.agentKey}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={pendingChannelId === channel.channelId}
                    aria-label="Remove channel mapping"
                    title="Remove mapping"
                    onClick={() => {
                      void handleRemoveMapping(channel.channelId);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
